// Where a `cyborg:typing_broadcast` lands, and when a cybo's line ends. Pure, so it is
// testable: app.svelte.ts registers a live client at module load and cannot be imported.
import type { TypingEvent, WorkSession } from "../core/types.js";
import { typingIsThreadScoped } from "./typing-scope.js";
import type { TranscriptInvariantReporter } from "../transcript-invariant.js";

interface TypingStore {
  addTyping(event: TypingEvent): void;
  endTyping(event: TypingEvent): void;
  endTypingAt(fromId: string, parentId: string): boolean;
}

export interface TypingSurfaces {
  thread: TypingStore & { parentId: string | null };
  channel: TypingStore & { activeId: string | null };
  dm: Pick<TypingStore, "addTyping"> & { activePeerId: string | null };
  dmSidebar: { mark(fromId: string): void };
  workSession: { add(event: TypingEvent): void };
}

type Surface = "thread" | "channel";

export function routeTypingEvent(
  event: TypingEvent,
  surfaces: TypingSurfaces,
  isMe: (id: string | undefined) => boolean,
): void {
  if (isMe(event.fromId)) return;
  const inOpenThread = !!event.parentId && event.parentId === surfaces.thread.parentId;
  const inChannel =
    !typingIsThreadScoped(event) && !event.toId && event.channelId === surfaces.channel.activeId;
  if (event.stopped === true) {
    if (inOpenThread) surfaces.thread.endTyping(event);
    if (inChannel) surfaces.channel.endTyping(event);
    return;
  }
  if (event.workSessionId) surfaces.workSession.add(event);
  if (inOpenThread) surfaces.thread.addTyping(event);
  if (event.parentId && typingIsThreadScoped(event)) return;
  if (event.toId) {
    if (!isMe(event.toId)) return;
    surfaces.dmSidebar.mark(event.fromId);
    if (event.fromId === surfaces.dm.activePeerId) surfaces.dm.addTyping(event);
    return;
  }
  if (inChannel) surfaces.channel.addTyping(event);
}

const TURN_ENDED: ReadonlySet<string> = new Set(["done", "paused", "blocked"]);

// The backstop for a daemon that sends no stop frame: the session settling IS its turn's
// end, so a live turn of that cybo answering under the session's anchor outlived it.
export function clearTypingOnSessionTurnEnd(
  prev: Pick<WorkSession, "status"> | undefined,
  next: WorkSession,
  surfaces: Pick<TypingSurfaces, "thread" | "channel">,
  invariants: Pick<TranscriptInvariantReporter, "report">,
): Surface[] {
  if (prev?.status !== "running" || !TURN_ENDED.has(next.status)) return [];
  const ended: Surface[] = [];
  if (next.anchorKind === "thread" && next.anchorId === surfaces.thread.parentId) {
    if (surfaces.thread.endTypingAt(next.cyboId, next.anchorId)) ended.push("thread");
  }
  if (next.channelId && next.channelId === surfaces.channel.activeId) {
    if (surfaces.channel.endTypingAt(next.cyboId, next.anchorId)) ended.push("channel");
  }
  for (const surface of ended) {
    invariants.report("typing_outlived_turn", {
      agentId: next.agentId,
      cyboId: next.cyboId,
      workSessionId: next.id,
      surface,
      status: next.status,
    });
  }
  return ended;
}
